/**
 * Narrows cache invalidation for the two debounced auto-save mutations so they
 * do not trigger a full refetch of every "PublicBid"-tagged query (including the
 * real bid list) on each 300–400 ms keystroke.
 *
 * Strategy: onQueryStarted patches the specific cached request in both the list
 * and the detail query directly from the mutation response DTO, and the broad
 * invalidatesTags is replaced with an empty array so the refetch storm is avoided.
 *
 * Safety: the bid-domain queries (listPublicBidsForEvent, getPublicBidForBuyer,
 * etc.) still provide/invalidate "PublicBid" as before — only the two debounced
 * participation auto-saves are narrowed.
 *
 * Note: bffApi.enhanceEndpoints() mutates the shared endpointDefinitions map in
 * place (Object.assign under the hood), so the original hooks from bff-api.ts
 * pick up these changes automatically once this module is imported.
 */
import { bffApi } from "../../autogen/bff-api";
import type {
  BuyerParticipationRequestDetailDto,
  BuyerParticipationRequestDto,
  UpdateParticipationRequestNotesApiArg,
  UpdateParticipationRequestSelectionDraftApiArg,
} from "../../autogen/bff-api";

bffApi.enhanceEndpoints({
  endpoints: {
    updateParticipationRequestNotes: {
      invalidatesTags: [],
      async onQueryStarted(
        arg: UpdateParticipationRequestNotesApiArg,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: detail } = await queryFulfilled;
          const updatedRequest = (detail as BuyerParticipationRequestDetailDto).request;
          if (!updatedRequest) return;

          // Patch the list query cache
          dispatch(
            bffApi.util.updateQueryData(
              "listParticipationRequestsForBuyer",
              { eventId: arg.eventId },
              (draft) => {
                if (!draft.requests) return;
                const idx = draft.requests.findIndex((r) => r.id === arg.requestId);
                if (idx !== -1) {
                  draft.requests[idx] = updatedRequest as BuyerParticipationRequestDto;
                }
              },
            ),
          );

          // Patch the detail query cache
          dispatch(
            bffApi.util.updateQueryData(
              "getParticipationRequestForBuyer",
              { eventId: arg.eventId, requestId: arg.requestId },
              (draft) => {
                draft.request = updatedRequest as BuyerParticipationRequestDto;
              },
            ),
          );
        } catch {
          // Network/server error — the existing error handling in the caller handles this.
        }
      },
    },

    updateParticipationRequestSelectionDraft: {
      invalidatesTags: [],
      async onQueryStarted(
        arg: UpdateParticipationRequestSelectionDraftApiArg,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedRequest } = await queryFulfilled;

          // Patch the list query cache
          dispatch(
            bffApi.util.updateQueryData(
              "listParticipationRequestsForBuyer",
              { eventId: arg.eventId },
              (draft) => {
                if (!draft.requests) return;
                const idx = draft.requests.findIndex((r) => r.id === arg.requestId);
                if (idx !== -1) {
                  draft.requests[idx] = updatedRequest as BuyerParticipationRequestDto;
                }
              },
            ),
          );

          // Patch the detail query cache
          dispatch(
            bffApi.util.updateQueryData(
              "getParticipationRequestForBuyer",
              { eventId: arg.eventId, requestId: arg.requestId },
              (draft) => {
                draft.request = updatedRequest as BuyerParticipationRequestDto;
              },
            ),
          );
        } catch {
          // Network/server error — the existing error handling in the caller handles this.
        }
      },
    },
  },
});
